package com.fantasytenis.web.components

import androidx.compose.runtime.*
import com.fantasytenis.shared.core.utils.SharedData
import com.fantasytenis.shared.domain.notifications.entity.NotificationEntity
import com.fantasytenis.shared.domain.notifications.entity.NotificationType
import com.fantasytenis.web.di.DependencyContainer
import com.fantasytenis.web.navigation.NavigationState
import com.fantasytenis.web.navigation.Route
import com.fantasytenis.web.theme.Theme
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLElement

/**
 * NotificationBell - Bell icon with unread count badge.
 * Clicking opens the NotificationDropdown.
 */
@Composable
fun NotificationBell(
    onClick: () -> Unit
) {
    val colors = Theme.colors
    val unreadCount by SharedData.unreadNotificationCount.collectAsState()

    Div({
        style {
            position(Position.Relative)
            cursor("pointer")
            padding(8.px)
            borderRadius(8.px)
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
        }
        onClick { onClick() }
        onMouseOver {
            (it.currentTarget as? HTMLElement)?.style?.backgroundColor =
                colors.surfaceVariant.toString()
        }
        onMouseOut {
            (it.currentTarget as? HTMLElement)?.style?.backgroundColor = "transparent"
        }
    }) {
        // Bell icon (using Unicode bell character)
        Span({
            style {
                fontSize(20.px)
                color(colors.textSecondary)
            }
        }) {
            Text("\uD83D\uDD14") // 🔔
        }

        // Unread badge
        if (unreadCount > 0) {
            Div({
                style {
                    position(Position.Absolute)
                    top(2.px)
                    right(2.px)
                    backgroundColor(Color("#F44336"))
                    color(Color.white)
                    fontSize(10.px)
                    fontWeight(700)
                    minWidth(16.px)
                    height(16.px)
                    borderRadius(8.px)
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    padding(0.px, 4.px)
                }
            }) {
                Text(if (unreadCount > 99) "99+" else unreadCount.toString())
            }
        }
    }
}

/**
 * NotificationDropdown - Dropdown panel showing recent notifications.
 */
@Composable
fun NotificationDropdown(
    isOpen: Boolean,
    onClose: () -> Unit,
    onNotificationClick: (NotificationEntity) -> Unit
) {
    if (!isOpen) return

    val colors = Theme.colors
    val notifications by SharedData.notifications.collectAsState()
    val scope = rememberCoroutineScope()
    var isLoading by remember { mutableStateOf(false) }

    // Load notifications when dropdown opens
    LaunchedEffect(isOpen) {
        if (isOpen && notifications.isEmpty()) {
            isLoading = true
            DependencyContainer.getNotificationsUseCase(limit = 10)
            isLoading = false
        }
    }

    // Backdrop to close dropdown
    Div({
        style {
            position(Position.Fixed)
            top(0.px)
            left(0.px)
            right(0.px)
            bottom(0.px)
            property("z-index", "999")
        }
        onClick { onClose() }
    })

    // Dropdown panel
    Div({
        style {
            position(Position.Absolute)
            top(100.percent)
            right(0.px)
            marginTop(8.px)
            width(360.px)
            maxHeight(480.px)
            backgroundColor(colors.surface)
            borderRadius(12.px)
            property("box-shadow", "0 8px 32px rgba(0, 0, 0, 0.2)")
            property("z-index", "1000")
            overflowY("auto")
            property("animation", "fadeIn 0.2s ease-out")
        }
        onClick { it.stopPropagation() }
    }) {
        // Header
        Div({
            style {
                padding(16.px)
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
                property("border-bottom", "1px solid ${colors.border}")
            }
        }) {
            H3({
                style {
                    margin(0.px)
                    fontSize(16.px)
                    fontWeight(600)
                    color(colors.textPrimary)
                }
            }) {
                Text("Notifications")
            }

            // Mark all as read button
            if (notifications.any { !it.read }) {
                Span({
                    style {
                        fontSize(12.px)
                        color(colors.primary)
                        cursor("pointer")
                    }
                    onClick {
                        scope.launch {
                            DependencyContainer.markAllNotificationsReadUseCase()
                        }
                    }
                }) {
                    Text("Mark all read")
                }
            }
        }

        // Content
        when {
            isLoading -> {
                Div({
                    style {
                        padding(32.px)
                        textAlign("center")
                        color(colors.textSecondary)
                    }
                }) {
                    Text("Loading...")
                }
            }
            notifications.isEmpty() -> {
                Div({
                    style {
                        padding(32.px)
                        textAlign("center")
                    }
                }) {
                    Div({
                        style {
                            fontSize(48.px)
                            marginBottom(12.px)
                        }
                    }) {
                        Text("\uD83D\uDD14") // 🔔
                    }
                    Div({
                        style {
                            color(colors.textSecondary)
                            fontSize(14.px)
                        }
                    }) {
                        Text("No notifications yet")
                    }
                }
            }
            else -> {
                notifications.take(10).forEach { notification ->
                    NotificationItem(
                        notification = notification,
                        onClick = {
                            scope.launch {
                                if (!notification.read) {
                                    DependencyContainer.markNotificationReadUseCase(notification.id)
                                }
                            }
                            onNotificationClick(notification)
                            onClose()
                        },
                        onDismiss = {
                            scope.launch {
                                DependencyContainer.deleteNotificationUseCase(notification.id)
                            }
                        }
                    )
                }

                // View all link
                if (notifications.size > 10) {
                    Div({
                        style {
                            padding(12.px, 16.px)
                            textAlign("center")
                            property("border-top", "1px solid ${colors.border}")
                        }
                    }) {
                        Span({
                            style {
                                color(colors.primary)
                                fontSize(14.px)
                                cursor("pointer")
                            }
                            onClick {
                                // Could navigate to a full notifications page
                                onClose()
                            }
                        }) {
                            Text("View all notifications")
                        }
                    }
                }
            }
        }
    }
}

/**
 * NotificationItem - Single notification row in the dropdown.
 */
@Composable
private fun NotificationItem(
    notification: NotificationEntity,
    onClick: () -> Unit,
    onDismiss: () -> Unit
) {
    val colors = Theme.colors
    var isHovered by remember { mutableStateOf(false) }

    Div({
        style {
            padding(12.px, 16.px)
            display(DisplayStyle.Flex)
            gap(12.px)
            alignItems(AlignItems.FlexStart)
            cursor("pointer")
            backgroundColor(
                when {
                    !notification.read -> Color(colors.surfaceVariant.toString())
                    isHovered -> Color(colors.surfaceVariant.toString())
                    else -> Color.transparent
                }
            )
            property("transition", "background-color 0.15s ease")
        }
        onClick { onClick() }
        onMouseOver { isHovered = true }
        onMouseOut { isHovered = false }
    }) {
        // Icon
        Div({
            style {
                width(36.px)
                height(36.px)
                borderRadius(50.percent)
                backgroundColor(Color(notification.accentColor + "20")) // 20% opacity
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                fontSize(18.px)
                flexShrink(0)
            }
        }) {
            Text(notification.icon)
        }

        // Content
        Div({
            style {
                flex(1)
                minWidth(0.px) // Allow text truncation
            }
        }) {
            // Title
            Div({
                style {
                    fontSize(14.px)
                    fontWeight(if (notification.read) 400 else 600)
                    color(colors.textPrimary)
                    marginBottom(2.px)
                    property("white-space", "nowrap")
                    property("overflow", "hidden")
                    property("text-overflow", "ellipsis")
                }
            }) {
                Text(notification.title)
            }

            // Message
            Div({
                style {
                    fontSize(13.px)
                    color(colors.textSecondary)
                    lineHeight("1.4")
                    property("display", "-webkit-box")
                    property("-webkit-line-clamp", "2")
                    property("-webkit-box-orient", "vertical")
                    property("overflow", "hidden")
                }
            }) {
                Text(notification.message)
            }

            // Timestamp
            Div({
                style {
                    fontSize(11.px)
                    color(colors.textSecondary)
                    marginTop(4.px)
                }
            }) {
                Text(formatRelativeTime(notification.createdAt))
            }
        }

        // Dismiss button (shown on hover)
        if (isHovered) {
            Div({
                style {
                    width(24.px)
                    height(24.px)
                    borderRadius(50.percent)
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    color(colors.textSecondary)
                    fontSize(14.px)
                    flexShrink(0)
                }
                onClick { event ->
                    event.stopPropagation()
                    onDismiss()
                }
                onMouseOver {
                    (it.currentTarget as? HTMLElement)?.style?.backgroundColor =
                        colors.surfaceVariant.toString()
                }
                onMouseOut {
                    (it.currentTarget as? HTMLElement)?.style?.backgroundColor = "transparent"
                }
            }) {
                Text("\u2715") // ✕
            }
        }

        // Unread indicator
        if (!notification.read) {
            Div({
                style {
                    width(8.px)
                    height(8.px)
                    borderRadius(50.percent)
                    backgroundColor(colors.primary)
                    flexShrink(0)
                    marginTop(6.px)
                }
            })
        }
    }
}

/**
 * Format timestamp to relative time string.
 */
private fun formatRelativeTime(isoTimestamp: String): String {
    return try {
        val now = js("Date.now()") as Double
        val then = kotlin.js.Date(isoTimestamp).getTime()
        val diffMs = now - then
        val diffMinutes = (diffMs / 60000).toLong()
        val diffHours = (diffMs / 3600000).toLong()
        val diffDays = (diffMs / 86400000).toLong()

        when {
            diffMinutes < 1 -> "Just now"
            diffMinutes < 60 -> "${diffMinutes}m ago"
            diffHours < 24 -> "${diffHours}h ago"
            diffDays < 7 -> "${diffDays}d ago"
            else -> "${diffDays / 7}w ago"
        }
    } catch (e: Exception) {
        "Recently"
    }
}

/**
 * NotificationCenter - Complete notification system with bell and dropdown.
 * Use this in the header for the full notification experience.
 */
@Composable
fun NotificationCenter() {
    var isDropdownOpen by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()

    // Fetch unread count on mount
    LaunchedEffect(Unit) {
        DependencyContainer.getUnreadCountUseCase()
    }

    Div({
        style {
            position(Position.Relative)
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
        }
    }) {
        NotificationBell(
            onClick = {
                if (!isDropdownOpen) {
                    // Refresh notifications when opening
                    scope.launch {
                        DependencyContainer.getNotificationsUseCase(limit = 10)
                    }
                }
                isDropdownOpen = !isDropdownOpen
            }
        )

        NotificationDropdown(
            isOpen = isDropdownOpen,
            onClose = { isDropdownOpen = false },
            onNotificationClick = { notification ->
                // Navigate based on notification type
                when (notification.type) {
                    NotificationType.CONTEST_COMPLETED,
                    NotificationType.PAYOUT_RECEIVED -> {
                        notification.contestId?.let { contestId ->
                            NavigationState.navigate(Route.ContestDetail(contestId))
                        }
                    }
                    NotificationType.ACHIEVEMENT_UNLOCKED -> {
                        // Navigate to profile or achievements page
                        NavigationState.navigate(Route.Profile)
                    }
                    NotificationType.STREAK_AT_RISK -> {
                        NavigationState.navigate(Route.Home)
                    }
                    NotificationType.CONTEST_REMINDER -> {
                        notification.contestId?.let { contestId ->
                            NavigationState.navigate(Route.ContestDetail(contestId))
                        }
                    }
                    NotificationType.GENERAL -> {
                        // Stay on current page
                    }
                }
            }
        )
    }
}
